import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * ScrollImageSequenceComponent
 * Maps scroll progress within the section to a frame index (0..N-1) and
 * toggles visibility of stacked frames. Optional per-frame overlay is shown
 * together with the main frame image. When scrolled beyond the sequence, the
 * page resumes normal scrolling.
 */
export class ScrollImageSequenceComponent extends Component {
  requiredRefs = ['container'];

  /** @type {HTMLElement[]} */ frames = [];
  /** @type {number} */ #currentIndex = -1;
  /** @type {number} */ #pxPerFrame = 40;
  /** @type {boolean} */ #active = false;
  /** @type {IntersectionObserver | null} */ #observer = null;
  /** @type {() => void} */ #onScrollBound = () => {};
  /** @type {() => void} */ #onResizeBound = () => {};

  connectedCallback() {
    super.connectedCallback();

    // Initialize fit
    const fit = this.dataset.fit === 'contain' ? 'contain' : 'cover';
    this.style.setProperty('--object-fit', fit);

    // Read pixels per frame
    const ppf = Number(this.dataset.pxPerFrame);
    if (!Number.isNaN(ppf) && ppf > 0) this.#pxPerFrame = ppf;

    // Collect frames
    this.frames = Array.from(this.querySelectorAll('[ref="frames[]"]'));

    // If reduced motion, show first frame and exit
    if (prefersReducedMotion()) {
      if (this.frames.length) this.#setIndex(0);
      return;
    }

    // Compute scroll length and set up observers/listeners
    this.#updateScrollLength();
    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: [0, 0.05, 0.5, 1] });
    this.#observer.observe(this);

    this.#onScrollBound = this.#onScroll.bind(this);
    this.#onResizeBound = this.#updateScrollLength.bind(this);
    window.addEventListener('scroll', this.#onScrollBound, { passive: true });
    window.addEventListener('resize', this.#onResizeBound);

    // Initial render in case already within viewport
    this.#onScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    window.removeEventListener('scroll', this.#onScrollBound);
    window.removeEventListener('resize', this.#onResizeBound);
  }

  /** Sets CSS --scroll-length based on number of frames. */
  #updateScrollLength = () => {
    const { container } = this.refs;
    const count = this.frames.length;
    const length = Math.max(0, Math.round(Math.max(0, count - 1) * this.#pxPerFrame));
    container.style.setProperty('--scroll-length', `${length}px`);
    this.#onScroll();
  };

  /** Intersection toggles whether we respond to scroll. */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.target !== this) continue;
      this.#active = entry.isIntersecting && entry.intersectionRatio > 0.05;
      if (this.#active) this.#onScroll();
    }
  };

  /** Map container scroll progress to a frame index and apply it. */
  #onScroll() {
    if (!this.#active) return;
    if (!this.frames.length) return;

    const { container } = this.refs;
    const rect = container.getBoundingClientRect();
    const viewport = window.innerHeight;
    const totalScrollable = Math.max(0, container.offsetHeight - viewport);
    const offset = Math.min(totalScrollable, Math.max(0, 0 - rect.top));
    const progress = totalScrollable > 0 ? offset / totalScrollable : 0;

    const maxIndex = this.frames.length - 1;
    const index = Math.round(progress * maxIndex);
    this.#setIndex(index);
  }

  /** Apply current frame index, toggle classes minimally. */
  #setIndex(index) {
    const clamped = Math.max(0, Math.min(index, this.frames.length - 1));
    if (clamped === this.#currentIndex) return;

    const prev = this.#currentIndex;
    this.#currentIndex = clamped;

    if (prev >= 0 && prev < this.frames.length) this.frames[prev].classList.remove('is-current');
    if (this.frames[clamped]) this.frames[clamped].classList.add('is-current');
  }
}

if (!customElements.get('scroll-image-sequence-component')) {
  customElements.define('scroll-image-sequence-component', ScrollImageSequenceComponent);
}


